import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Archive, Trash2, RotateCcw, CheckCircle2, AlertTriangle, Clock, Pencil, X, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatTRY, formatDate, monthKey, monthLabel, todayISO } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import MoneyInput from "@/components/MoneyInput";

const STATUS_LABEL = { bekliyor: "Bekliyor", odendi: "Ödendi", karsiliksiz: "Karşılıksız" };
const STATUS_TONE = {
  bekliyor: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  odendi: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  karsiliksiz: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]",
};

const EMPTY_FORM = {
  type: "alinan", instrument: "cek", amount: 0, due_date: todayISO(),
  status: "bekliyor", drawer: "", bank: "", cari_id: "", description: "",
};

// Tab logic:
// - "pending": is_deleted=false + status=bekliyor
// - "paid":    is_deleted=false + status in [odendi, karsiliksiz]
// - "archive": is_deleted=true (all statuses)
export default function Cekler() {
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState("pending");
  const [typeFilter, setTypeFilter] = useState("all");
  const [checks, setChecks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [cariler, setCariler] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    const q = new URLSearchParams({ archived: tab === "archive" ? "true" : "false" });
    if (typeFilter !== "all") q.set("type", typeFilter);
    const [{ data: c }, { data: s }, { data: cr }] = await Promise.all([
      api.get(`/checks?${q.toString()}`),
      api.get("/checks/summary"),
      api.get("/cariler"),
    ]);
    // Filter by status tab
    const filtered = tab === "pending"
      ? c.filter((x) => x.status === "bekliyor")
      : tab === "paid"
        ? c.filter((x) => x.status === "odendi" || x.status === "karsiliksiz")
        : c; // archive shows all
    setChecks(filtered);
    setSummary(s);
    setCariler(cr);
    setSelected(new Set());
  }, [tab, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    checks.forEach((c) => {
      const k = monthKey(c.due_date);
      if (!g[k]) g[k] = { key: k, items: [], total: 0, alinan: 0, verilen: 0 };
      g[k].items.push(c);
      g[k].total += c.amount;
      if (c.type === "alinan") g[k].alinan += c.amount;
      else g[k].verilen += c.amount;
    });
    return Object.values(g).sort((a, b) => a.key.localeCompare(b.key));
  }, [checks]);

  const allIds = useMemo(() => checks.map((c) => c.id), [checks]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      type: c.type, instrument: c.instrument, amount: c.amount, due_date: c.due_date,
      status: c.status, drawer: c.drawer || "", bank: c.bank || "",
      cari_id: c.cari_id || "", description: c.description || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount), cari_id: form.cari_id || null };
      if (editingId) {
        await api.patch(`/checks/${editingId}`, payload);
        toast.success("Çek güncellendi");
      } else {
        await api.post("/checks", payload);
        toast.success("Çek eklendi");
      }
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/checks/${id}/status?status=${status}`);
      toast.success("Durum güncellendi");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bulkArchive = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} çeki arşive taşımak istediğinize emin misiniz?`)) return;
    await api.post("/checks/bulk-delete", { ids: Array.from(selected) });
    toast.success("Arşivlendi");
    load();
  };

  const bulkMarkPaid = async () => {
    if (selected.size === 0) return;
    await api.post("/checks/bulk-status", { ids: Array.from(selected), status: "odendi" });
    toast.success("Ödendi olarak işaretlendi");
    load();
  };

  const bulkMarkPending = async () => {
    if (selected.size === 0) return;
    await api.post("/checks/bulk-status", { ids: Array.from(selected), status: "bekliyor" });
    toast.success("Bekliyor olarak işaretlendi");
    load();
  };

  const bulkRestore = async () => {
    if (selected.size === 0) return;
    await api.post("/checks/bulk-restore", { ids: Array.from(selected) });
    toast.success("Geri getirildi");
    load();
  };

  const bulkPurge = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} çeki KALICI olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    await api.post("/checks/bulk-purge", { ids: Array.from(selected) });
    toast.success("Kalıcı olarak silindi");
    load();
  };

  const restore = async (id) => {
    await api.post(`/checks/${id}/restore`);
    toast.success("Geri getirildi");
    load();
  };

  const purge = async (id) => {
    if (!window.confirm("Bu çeki KALICI olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.")) return;
    await api.delete(`/checks/${id}/purge`);
    toast.success("Kalıcı olarak silindi");
    load();
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => (prev.size === allIds.length ? new Set() : new Set(allIds)));
  };

  const today = summary?.today || todayISO();
  const allChecked = allIds.length > 0 && selected.size === allIds.length;
  const canBulkAct = selected.size > 0;

  return (
    <div className="space-y-6" data-testid="cekler-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Finans</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Çekler & Senetler</h1>
        </div>
        <div className="flex gap-2">
          {hasPerm("cekler.view") && (
            <Button
              variant="outline"
              data-testid="check-export-btn"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/checks/excel?archived=${tab === "archive"}`, "_blank", "noopener")}
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel İndir
            </Button>
          )}
          {hasPerm("cekler.create") && (
            <Button data-testid="new-check-btn" onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" /> Yeni Çek
            </Button>
          )}
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 shadow-none rounded-md border-l-4 border-l-[hsl(var(--warning))]">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-[hsl(var(--warning))]" />
              <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Alınan Bekleyen</span>
            </div>
            <div className="font-heading text-xl font-bold tabular-nums">{formatTRY(summary.alinan_bekleyen)}</div>
          </Card>
          <Card className="p-4 shadow-none rounded-md border-l-4 border-l-[hsl(var(--warning))]">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-[hsl(var(--warning))]" />
              <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Verilen Bekleyen</span>
            </div>
            <div className="font-heading text-xl font-bold tabular-nums">{formatTRY(summary.verilen_bekleyen)}</div>
          </Card>
          <Card className="p-4 shadow-none rounded-md border-l-4 border-l-[hsl(var(--danger))]">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-[hsl(var(--danger))]" />
              <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Gecikmiş</span>
            </div>
            <div className="font-heading text-xl font-bold tabular-nums text-[hsl(var(--danger))]">{formatTRY(summary.gecikmis)}</div>
          </Card>
          <Card className="p-4 shadow-none rounded-md border-l-4 border-l-[hsl(var(--success))]">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" />
              <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Ödenen (Alınan)</span>
            </div>
            <div className="font-heading text-xl font-bold tabular-nums">{formatTRY(summary.odenen_alinan)}</div>
          </Card>
        </div>
      )}

      <div className="flex gap-3 flex-wrap items-center">
        <Tabs value={tab} onValueChange={setTab} data-testid="checks-tabs">
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending"><Clock className="w-3 h-3 mr-1" /> Bekleyen</TabsTrigger>
            <TabsTrigger value="paid" data-testid="tab-paid"><CheckCircle2 className="w-3 h-3 mr-1" /> Ödenen</TabsTrigger>
            <TabsTrigger value="archive" data-testid="tab-archive"><Archive className="w-3 h-3 mr-1" /> Arşiv</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40" data-testid="check-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="alinan">Alınan</SelectItem>
            <SelectItem value="verilen">Verilen</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk actions toolbar */}
      {canBulkAct && (
        <Card className="p-3 shadow-none rounded-md border-brand/40 bg-brand/5 flex flex-wrap items-center gap-2" data-testid="bulk-toolbar">
          <span className="text-sm font-medium mr-2">{selected.size} seçili</span>
          {tab === "pending" && hasPerm("cekler.edit") && (
            <Button size="sm" variant="outline" onClick={bulkMarkPaid} data-testid="bulk-mark-paid">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Ödendi işaretle
            </Button>
          )}
          {tab === "paid" && hasPerm("cekler.edit") && (
            <Button size="sm" variant="outline" onClick={bulkMarkPending} data-testid="bulk-mark-pending">
              <Clock className="w-3 h-3 mr-1" /> Bekliyor işaretle
            </Button>
          )}
          {tab !== "archive" && hasPerm("cekler.delete") && (
            <Button size="sm" variant="destructive" onClick={bulkArchive} data-testid="bulk-archive">
              <Archive className="w-3 h-3 mr-1" /> Arşivle
            </Button>
          )}
          {tab === "archive" && hasPerm("cekler.edit") && (
            <Button size="sm" variant="outline" onClick={bulkRestore} data-testid="bulk-restore">
              <RotateCcw className="w-3 h-3 mr-1" /> Geri Getir
            </Button>
          )}
          {tab === "archive" && hasPerm("cekler.delete") && (
            <Button size="sm" variant="destructive" onClick={bulkPurge} data-testid="bulk-purge">
              <Trash2 className="w-3 h-3 mr-1" /> Kalıcı Sil
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="ml-auto">
            <X className="w-3 h-3 mr-1" /> Seçimi Temizle
          </Button>
        </Card>
      )}

      <div className="space-y-6">
        {grouped.map((g) => (
          <div key={g.key} data-testid={`check-group-${g.key}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg font-semibold tracking-tight">{monthLabel(g.key)}</h3>
              <div className="flex items-center gap-4 text-xs">
                <span className="text-muted-foreground">Alınan: <span className="tabular-nums font-medium text-foreground">{formatTRY(g.alinan)}</span></span>
                <span className="text-muted-foreground">Verilen: <span className="tabular-nums font-medium text-foreground">{formatTRY(g.verilen)}</span></span>
              </div>
            </div>
            <Card className="shadow-none rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="w-10">
                        <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="select-all-checkbox" title="Tümünü seç" />
                      </TableHead>
                      <TableHead>Vade</TableHead>
                      <TableHead>Tür</TableHead>
                      <TableHead>Keşideci</TableHead>
                      <TableHead>Banka</TableHead>
                      <TableHead>Cari</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead className="text-right">Tutar</TableHead>
                      <TableHead className="text-right w-40">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {g.items.map((c) => {
                      const overdue = c.status === "bekliyor" && c.due_date < today;
                      return (
                        <TableRow key={c.id} data-testid={`check-row-${c.id}`} className={overdue ? "bg-[hsl(var(--danger))]/5" : "hover:bg-secondary/40"}>
                          <TableCell>
                            <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`select-${c.id}`} />
                          </TableCell>
                          <TableCell className="tabular-nums text-xs">{formatDate(c.due_date)}</TableCell>
                          <TableCell className="text-xs">
                            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${c.type === "alinan" ? "bg-brand/15 text-brand" : "bg-secondary text-muted-foreground"}`}>
                              {c.type === "alinan" ? "Alınan" : "Verilen"} {c.instrument === "senet" ? "· Senet" : ""}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm">{c.drawer || "-"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{c.bank || "-"}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{cariler.find((x) => x.id === c.cari_id)?.name || "-"}</TableCell>
                          <TableCell>
                            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${overdue ? "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]" : STATUS_TONE[c.status]}`}>
                              {overdue ? "Gecikmiş" : STATUS_LABEL[c.status]}
                            </span>
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-medium">{formatTRY(c.amount)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {tab === "pending" && hasPerm("cekler.edit") && (
                                <Button size="sm" variant="outline" onClick={() => updateStatus(c.id, "odendi")} data-testid={`mark-paid-${c.id}`} className="h-7 text-xs">
                                  Ödendi
                                </Button>
                              )}
                              {tab === "paid" && hasPerm("cekler.edit") && (
                                <Button size="sm" variant="ghost" onClick={() => updateStatus(c.id, "bekliyor")} data-testid={`mark-pending-${c.id}`} className="h-7 text-xs">
                                  Bekliyor
                                </Button>
                              )}
                              {tab !== "archive" && hasPerm("cekler.edit") && (
                                <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-check-${c.id}`} className="h-7 w-7 p-0" title="Düzenle">
                                  <Pencil className="w-3 h-3" />
                                </Button>
                              )}
                              {tab === "archive" && hasPerm("cekler.edit") && (
                                <Button size="sm" variant="ghost" onClick={() => restore(c.id)} data-testid={`restore-${c.id}`} className="h-7 w-7 p-0" title="Geri Getir">
                                  <RotateCcw className="w-3 h-3" />
                                </Button>
                              )}
                              {tab === "archive" && hasPerm("cekler.delete") && (
                                <Button size="sm" variant="ghost" onClick={() => purge(c.id)} data-testid={`purge-${c.id}`} className="h-7 w-7 p-0 text-destructive" title="Kalıcı Sil">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        ))}
        {grouped.length === 0 && (
          <div className="text-sm text-muted-foreground p-12 text-center border border-dashed border-border rounded-md">
            {tab === "archive" ? "Arşivde kayıt yok." : tab === "paid" ? "Ödenen çek yok." : "Bekleyen çek yok."}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(EMPTY_FORM); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Çek / Senet Düzenle" : "Yeni Çek / Senet"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Tür</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="check-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alinan">Alınan</SelectItem>
                    <SelectItem value="verilen">Verilen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Enstrüman</Label>
                <Select value={form.instrument} onValueChange={(v) => setForm({ ...form, instrument: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cek">Çek</SelectItem>
                    <SelectItem value="senet">Senet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Tutar</Label><MoneyInput data-testid="check-amount" value={form.amount} onChange={(n) => setForm({ ...form, amount: n })} /></div>
              <div className="space-y-2"><Label>Vade Tarihi</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Keşideci</Label><Input value={form.drawer} onChange={(e) => setForm({ ...form, drawer: e.target.value })} /></div>
              <div className="space-y-2"><Label>Banka</Label><Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Cari</Label>
                <Select value={form.cari_id || "none"} onValueChange={(v) => setForm({ ...form, cari_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Yok —</SelectItem>
                    {cariler.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="check-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bekliyor">Bekliyor</SelectItem>
                    <SelectItem value="odendi">Ödendi</SelectItem>
                    <SelectItem value="karsiliksiz">Karşılıksız</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Açıklama</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="check-save" onClick={save} disabled={!form.amount}>{editingId ? "Kaydet" : "Ekle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
